import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { Editor } from "../components/Editor";
import { EmptyState } from "../components/EmptyState";
import { Badge } from "../components/ui/badge";
import { Users } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Session storage key for last viewed common file
const LAST_COMMON_FILE_KEY = "notekeeper_last_common_file";

// Funcție ajutătoare pentru a coda corect rutele (păstrează slash-urile pentru backend)
const encodePath = (path) => {
  return path.split('/').map(encodeURIComponent).join('/');
};

export default function CommonNotesPage({ sidebarOpen = true }) {
  // Notes state - exactly like My Notes
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionRestored, setSessionRestored] = useState(false);

  const fetchFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/common/files`);
      setFiles(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch files:", error);
      toast.error("Failed to load common notes");
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Restore last session file
  const restoreLastFile = useCallback(async (fileList) => {
    if (sessionRestored) return;
    
    try {
      const lastFilePath = sessionStorage.getItem(LAST_COMMON_FILE_KEY);
      if (lastFilePath && fileList.length > 0) {
        const findFile = (items, path) => {
          for (const item of items) {
            if (item.path === path) return true;
            if (item.children && findFile(item.children, path)) return true;
          }
          return false;
        };
        
        if (findFile(fileList, lastFilePath)) {
          await loadFile(lastFilePath);
        }
      }
    } catch (e) {
      console.error("Failed to restore session:", e);
    }
    setSessionRestored(true);
  }, [sessionRestored]);

  useEffect(() => {
    fetchFiles().then(fileList => {
      if (fileList.length > 0) {
        restoreLastFile(fileList);
      }
    });
  }, [fetchFiles, restoreLastFile]);

  // Save last file to session when it changes
  useEffect(() => {
    if (selectedFile?.path) {
      sessionStorage.setItem(LAST_COMMON_FILE_KEY, selectedFile.path);
    }
  }, [selectedFile]);

  const loadFile = async (path) => {
    try {
      // Folosim encodePath în loc de encodeURIComponent
      const response = await axios.get(`${API_URL}/common/files/${encodePath(path)}`);
      setSelectedFile({ path, name: path.split("/").pop() });
      setFileContent(response.data.content);
    } catch (error) {
      toast.error("Failed to load file");
    }
  };

  const saveFile = async (content) => {
    if (!selectedFile) return;
    
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/common/files/${encodePath(selectedFile.path)}`, {
        content
      });
      // DO NOT set fileContent here - prevents losing edits made during save
    } catch (error) {
      toast.error("Failed to save file");
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const createFile = async (name, parentPath = "", isFolder = false) => {
    // Siguranță: Sidebar-ul ar putea trimite argumentele în altă ordine (name, isFolder)
    let actualIsFolder = isFolder;
    let actualParentPath = parentPath;

    if (typeof parentPath === "boolean") {
      actualIsFolder = parentPath;
      actualParentPath = "";
    }

    // SECRETUL AICI: Dacă nu e specificat un folder părinte, dar ai un fișier selectat,
    // preluăm folderul acelui fișier!
    if (!actualParentPath && selectedFile?.path) {
      const pathParts = selectedFile.path.split("/");
      pathParts.pop(); // Scoatem numele fișierului
      actualParentPath = pathParts.join("/"); // Păstrăm doar folderul
    }

    try {
      const response = await axios.post(`${API_URL}/common/files`, {
        name: actualIsFolder ? name : (name.endsWith(".md") ? name : `${name}.md`),
        parent_path: actualParentPath,
        is_folder: actualIsFolder,
        content: ""
      });
      fetchFiles();
      if (!actualIsFolder) {
        loadFile(response.data.path);
      }
      toast.success(actualIsFolder ? "Folder created" : "Note created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create");
    }
  };

  const deleteFile = async (path) => {
    // Adăugăm fereastra de confirmare nativă
    const isConfirmed = window.confirm("Ești sigur că vrei să ștergi acest element?");
    
    // Oprim funcția dacă se apasă Cancel
    if (!isConfirmed) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/common/files/${encodePath(path)}`);
      if (selectedFile?.path === path) {
        setSelectedFile(null);
        setFileContent("");
      }
      fetchFiles();
      toast.success("Moved to recycle bin");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const renameFile = async (oldPath, newName) => {
    try {
      await axios.post(`${API_URL}/common/files/rename`, {
        old_path: oldPath,
        new_name: newName
      });
      fetchFiles();
      toast.success("Renamed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename");
    }
  };

  const moveFile = async (sourcePath, destPath) => {
    try {
      await axios.post(`${API_URL}/common/files/move`, {
        source_path: sourcePath,
        dest_path: destPath
      });
      fetchFiles();
      toast.success("Moved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to move");
    }
  };

  const togglePin = async (path) => {
    try {
      await axios.post(`${API_URL}/common/files/pin`, { file_path: path });
      fetchFiles();
    } catch (error) {
      toast.error("Failed to toggle pin");
    }
  };

  return (
    <div className="flex-1 flex overflow-hidden" data-testid="common-notes-page">
      {/* Sidebar - controlled by parent */}
      <Sidebar
        files={files}
        selectedFile={selectedFile}
        isOpen={sidebarOpen}
        isLoading={isLoading}
        onSelectFile={loadFile}
        onCreateFile={createFile}
        onDeleteFile={deleteFile}
        onRenameFile={renameFile}
        onMoveFile={moveFile}
        onTogglePin={togglePin}
      />
      
      {/* Main Editor Area */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Shared indicator bar */}
        <div className="px-4 py-2 bg-blue-500/10 border-b border-blue-500/20 flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-500" />
          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Common Notes</span>
          <Badge variant="secondary" className="text-xs hidden sm:inline-flex">Shared with all users</Badge>
        </div>
        
        {selectedFile ? (
          <div className="flex-1 overflow-hidden">
            <div className="h-full max-w-4xl mx-auto px-4 md:px-0">
              <Editor
                key={selectedFile.path}
                content={fileContent}
                fileName={selectedFile.name}
                onSave={saveFile}
                isSaving={isSaving}
                fileContext="common"
              />
            </div>
          </div>
        ) : (
          <EmptyState onCreateFile={() => createFile("Untitled")} />
        )}
      </main>
    </div>
  );
}